import ExclamationCircleIcon from '@heroicons/react/20/solid/ExclamationCircleIcon.js';
import React from 'react';

import { tv } from '../../tv';
import { Text } from '../text';

/**
 * A form-level error: the submission failed, as opposed to one field being
 * invalid.
 *
 * Field-level errors are a different problem and already have an owner. They
 * belong to the field, which knows its own id and can wire `aria-describedby`
 * and `aria-invalid` to the input. A form-level error has no field to attach
 * to, so nothing announces it, which is why it needs `role="alert"` and this
 * component needs to exist.
 *
 * Every system surveyed routes errors through a dedicated component rather
 * than a colour on the text primitive, INCLUDING the three that ship a tone
 * prop (Polaris `tone`, Ant `type`, MUI `color`). None of their docs tell you
 * to write an error as coloured text. The reason is that an error is three
 * things and colour is only one of them: the message, the announcement, and
 * the non-colour indicator. A tone prop delivers the last of those and leaves
 * the caller to remember the rest.
 *
 * The icon is not decoration. WCAG 1.4.1 F81 is a named failure for marking
 * an error with colour alone, and Polaris and Carbon both bake an icon in for
 * this reason. It carries `aria-hidden` because the text already says it.
 */

const errorStyles = tv({
  slots: {
    root: 'ck:flex ck:gap-2 ck:text-error-text',
    icon: 'ck:size-5 ck:shrink-0',
  },
  variants: {
    /* Centred is the form-level default: these sit under a submit button,
     * spanning the form rather than hugging one control. */
    align: {
      start: { root: 'ck:justify-start ck:text-left' },
      center: { root: 'ck:justify-center ck:text-center' },
    },
  },
  defaultVariants: { align: 'center' },
});

export interface FormErrorProps {
  children?: React.ReactNode;
  /** Aligns the message within the form. */
  align?: 'start' | 'center';
}

export const FormError = React.forwardRef<HTMLDivElement, FormErrorProps>(function FormError(
  { children, align = 'center' },
  ref,
) {
  const { root, icon } = errorStyles({ align });

  if (!children) return null;

  return (
    /* role="alert" rather than an aria-describedby link: there is no field to
     * describe, and the message appears after the user has already submitted,
     * so nothing else would announce it. */
    <div ref={ref} className={root()} role="alert" data-component="form-error">
      <ExclamationCircleIcon className={icon()} aria-hidden />
      <Text size="sm" color="inherit" align={align}>
        {children}
      </Text>
    </div>
  );
});
